import type { FC, ReactNode } from 'react'
import type { EnquiryPrefill } from '@/types'

import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState
} from 'react'

import { useSmoothScroll } from '@/components/generals/smooth-scroll'
import { usePageTransition } from '@/components/generals/page-transition'
import { BOOKING_HREF } from '@/constants/site'

interface EnquiryRequest {
    prefill: EnquiryPrefill
    /** Changes on every request, so asking twice for the same thing still reaches the form. */
    nonce: number
}

interface EnquiryContextValue {
    request: EnquiryRequest | null
    requestEnquiry: (prefill: EnquiryPrefill) => void
}

interface EnquiryProviderProps {
    children: ReactNode
}

const EnquiryContext = createContext<EnquiryContextValue>({
    request: null,
    requestEnquiry: () => undefined
})

/**
 * Lets any section on any page pre-fill the booking form. On a page that has the
 * form it glides down to it; anywhere else it travels to the visit page first.
 */
export const EnquiryProvider: FC<EnquiryProviderProps> = ({ children }): ReactNode => {
    const [request, setRequest] = useState<EnquiryRequest | null>(null)
    const { scrollTo } = useSmoothScroll()
    const { navigate } = usePageTransition()

    const requestEnquiry = useCallback((prefill: EnquiryPrefill): void => {
        setRequest((previous) => ({ prefill, nonce: (previous?.nonce ?? 0) + 1 }))

        if (!document.getElementById('booking')) {
            navigate(BOOKING_HREF)
            return
        }

        scrollTo('#booking', {
            offset: -24,
            onComplete: () => {
                document.getElementById('booking-form')?.focus({ preventScroll: true })
            }
        })
    }, [navigate, scrollTo])

    const value = useMemo(() => ({ request, requestEnquiry }), [request, requestEnquiry])

    return (
        <EnquiryContext.Provider value={value}>
            {children}
        </EnquiryContext.Provider>
    )
}

export const useEnquiry = (): EnquiryContextValue => useContext(EnquiryContext)
